import { useState } from "react";
import { Plus, Trash2, ChevronDown, MapPin, Star, Building2, QrCode, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { downloadTempleKit } from "@/api/client";

const inputCls =
  "w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm text-amber-50 placeholder:text-amber-100/30 outline-none focus:border-amber-300/50";

const F = ({ label, ...p }) => (
  <div>
    {label && <label className="mb-1 block text-[11px] text-amber-100/50">{label}</label>}
    <input {...p} className={inputCls} />
  </div>
);

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

// Temples = sub-locations under a campaign. Each has its own URL (/{slug}) and
// optional local CTA overrides; they inherit the campaign's page & journeys.
export const TemplesPanel = ({ temples = [], onChange, campaignId }) => {
  const [open, setOpen] = useState(null);
  const [kitBusy, setKitBusy] = useState(null);

  const downloadKit = async (slug) => {
    if (!slug) return;
    setKitBusy(slug);
    try {
      await downloadTempleKit(campaignId, slug);
      toast.success("Temple QR kit downloaded");
    } catch (e) {
      const msg = e?.response?.status === 404
        ? "Save the campaign first, then download this temple's kit"
        : "Could not download this temple's kit";
      toast.error(msg);
    } finally {
      setKitBusy(null);
    }
  };

  const update = (i, patch) => onChange(temples.map((t, ti) => (ti === i ? { ...t, ...patch } : t)));
  const remove = (i) => onChange(temples.filter((_, ti) => ti !== i));
  const add = () => {
    const t = {
      id: `temple-${Date.now()}`,
      name: "",
      slug: "",
      contact: "",
      active: true,
      ctas: [],
    };
    onChange([...temples, t]);
    setOpen(temples.length);
  };

  const setCta = (ti, ci, patch) =>
    update(ti, { ctas: temples[ti].ctas.map((c, i) => (i === ci ? { ...c, ...patch } : c)) });
  const addCta = (ti) =>
    update(ti, {
      ctas: [
        ...(temples[ti].ctas || []),
        {
          key: `cta-${Date.now()}`,
          icon: "sparkles",
          image: "",
          primary: (temples[ti].ctas || []).length === 0,
          title_en: "",
          title_hi: "",
          desc_en: "",
          desc_hi: "",
          label_en: "",
          label_hi: "",
          url: "#",
        },
      ],
    });
  const removeCta = (ti, ci) => update(ti, { ctas: temples[ti].ctas.filter((_, i) => i !== ci) });

  return (
    <div className="mt-8">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-amber-300" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-amber-100/80">
            Temples / Locations ({temples.length})
          </h2>
        </div>
        <button
          data-testid="add-temple-btn"
          onClick={add}
          className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/25 px-3.5 py-1.5 text-xs font-medium text-amber-200 transition-all hover:border-amber-300/60 hover:bg-amber-400/10"
        >
          <Plus className="h-3.5 w-3.5" /> Add temple
        </button>
      </div>
      <p className="mb-4 text-xs text-amber-100/40">
        Each temple gets its own URL (e.g. <span className="text-amber-200/70">/juhu</span>) and can
        override the CTAs; it inherits this campaign&apos;s page &amp; journeys. Remember to click
        &quot;Save changes&quot; above.
      </p>

      {temples.length === 0 && (
        <p className="rounded-2xl border border-dashed border-amber-300/15 py-8 text-center text-sm text-amber-100/40">
          No temples yet. Add one to publish a location page.
        </p>
      )}

      <div className="space-y-3">
        {temples.map((t, i) => {
          const isOpen = open === i;
          return (
            <div
              key={t.id || i}
              data-testid={`temple-row-${i}`}
              className="overflow-hidden rounded-2xl border border-amber-300/12 bg-[#0d1226]/70"
            >
              <div className="flex items-center gap-3 px-5 py-4">
                <MapPin className="h-4 w-4 shrink-0 text-amber-300/70" />
                <button
                  onClick={() => setOpen(isOpen ? null : i)}
                  data-testid={`temple-toggle-${i}`}
                  className="flex flex-1 items-center gap-3 text-left"
                >
                  <span className="font-display text-lg text-amber-200">
                    {t.name || "Untitled temple"}
                  </span>
                  {t.slug && (
                    <span className="rounded-full bg-black/40 px-2 py-0.5 text-[11px] text-amber-100/60">
                      /{t.slug}
                    </span>
                  )}
                </button>
                <label className="flex items-center gap-1.5 text-[11px] text-amber-100/60">
                  <input
                    type="checkbox"
                    data-testid={`temple-active-${i}`}
                    checked={t.active !== false}
                    onChange={(e) => update(i, { active: e.target.checked })}
                    className="h-3.5 w-3.5 accent-amber-400"
                  />
                  Live
                </label>
                <button
                  data-testid={`temple-kit-${i}`}
                  onClick={() => downloadKit(t.slug)}
                  disabled={!t.slug || kitBusy === t.slug}
                  title="Download this temple's QR kit"
                  className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/25 px-3 py-1.5 text-[11px] font-medium text-amber-200 transition-all hover:border-amber-300/60 hover:bg-amber-400/10 disabled:opacity-40"
                >
                  {kitBusy === t.slug ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <QrCode className="h-3.5 w-3.5" />
                  )}
                  Kit
                </button>
                <button
                  data-testid={`temple-remove-${i}`}
                  onClick={() => remove(i)}
                  className="rounded-full border border-red-400/20 p-1.5 text-red-300/80 hover:bg-red-500/10"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
                <ChevronDown
                  className={`h-4 w-4 text-amber-100/40 transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              </div>

              {isOpen && (
                <div className="space-y-4 border-t border-amber-300/10 p-5">
                  <div className="grid gap-4 sm:grid-cols-3">
                    <F
                      label="Temple name"
                      data-testid={`temple-name-${i}`}
                      value={t.name || ""}
                      onChange={(e) =>
                        update(i, {
                          name: e.target.value,
                          slug: t.slug || slugify(e.target.value),
                        })
                      }
                    />
                    <F
                      label="URL slug"
                      data-testid={`temple-slug-${i}`}
                      value={t.slug || ""}
                      onChange={(e) => update(i, { slug: slugify(e.target.value) })}
                      placeholder="juhu"
                    />
                    <F
                      label="Local contact / WhatsApp"
                      value={t.contact || ""}
                      onChange={(e) => update(i, { contact: e.target.value })}
                      placeholder="e.g. 919876543210"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-100/60">
                      Local CTA cards ({(t.ctas || []).length}) — leave empty to inherit campaign cards
                    </p>
                    <button
                      data-testid={`temple-add-cta-${i}`}
                      onClick={() => addCta(i)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/25 px-3 py-1 text-[11px] text-amber-200 hover:bg-amber-400/10"
                    >
                      <Plus className="h-3 w-3" /> Add card
                    </button>
                  </div>

                  <div className="space-y-3">
                    {(t.ctas || []).map((cta, ci) => (
                      <div key={ci} className="rounded-xl border border-amber-300/12 bg-[#080b16]/40 p-4">
                        <div className="mb-3 flex items-center justify-between">
                          <span className="text-xs text-amber-100/60">Card {ci + 1}</span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() =>
                                update(i, {
                                  ctas: t.ctas.map((x, xi) => ({ ...x, primary: xi === ci })),
                                })
                              }
                              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] ${
                                cta.primary
                                  ? "bg-amber-400 text-[#0a0f24]"
                                  : "border border-amber-300/25 text-amber-200 hover:bg-amber-400/10"
                              }`}
                            >
                              <Star className="h-3 w-3" /> {cta.primary ? "Primary" : "Make primary"}
                            </button>
                            <button
                              onClick={() => removeCta(i, ci)}
                              className="rounded-full border border-red-400/20 p-1 text-red-300/80 hover:bg-red-500/10"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <F label="Title (EN)" value={cta.title_en} onChange={(e) => setCta(i, ci, { title_en: e.target.value })} />
                          <F label="Title (HI)" value={cta.title_hi} onChange={(e) => setCta(i, ci, { title_hi: e.target.value })} />
                          <F label="Description (EN)" value={cta.desc_en} onChange={(e) => setCta(i, ci, { desc_en: e.target.value })} />
                          <F label="Description (HI)" value={cta.desc_hi} onChange={(e) => setCta(i, ci, { desc_hi: e.target.value })} />
                          <F label="Button label (EN)" value={cta.label_en} onChange={(e) => setCta(i, ci, { label_en: e.target.value })} />
                          <F label="Button label (HI)" value={cta.label_hi} onChange={(e) => setCta(i, ci, { label_hi: e.target.value })} />
                          <F label="Link URL" data-testid={`temple-cta-url-${i}-${ci}`} value={cta.url} onChange={(e) => setCta(i, ci, { url: e.target.value })} placeholder="https://…" />
                          <F label="Image URL / path" value={cta.image} onChange={(e) => setCta(i, ci, { image: e.target.value })} placeholder="/images/cards/…" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
